import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { currency, INVOICE_STATUS } from "@/lib/portalConstants";
import { BUSINESS } from "@/data/site";
import { printInvoice, printReceipt } from "@/lib/printTemplates";
import { InvoiceDialog } from "@/components/portal/InvoiceDialog";
import { format } from "date-fns";
import { Plus, Trash2, Loader2, FileText, Printer, Pencil, Send, Eye, ReceiptText, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState({ open: false, edit: null });
  const [viewInv, setViewInv] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const [payForm, setPayForm] = useState({ method: "Cash", paid_by: "", paid_at: format(new Date(), "yyyy-MM-dd") });

  const load = async () => {
    const [inv, c, s] = await Promise.all([
      api.get("/portal/invoices"),
      api.get("/portal/clients"),
      api.get("/portal/service-items"),
    ]);
    setInvoices(inv.data);
    setClients(c.data);
    setServices(s.data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    if (!loading && clients.length === 0) return toast.error("Add a client first.");
    setEditor({ open: true, edit: null });
  };
  const openEdit = (inv) => setEditor({ open: true, edit: inv });

  const changeStatus = async (inv, status) => {
    if (status === "paid" && inv.status !== "paid") {
      setPayForm({ method: "Cash", paid_by: inv.client?.contact_name || "", paid_at: format(new Date(), "yyyy-MM-dd") });
      setPayInv(inv);
      return;
    }
    await api.patch(`/portal/invoices/${inv.id}/status?status=${status}`);
    load();
  };
  const confirmPayment = async () => {
    if (!payInv) return;
    const id = payInv.id;
    try {
      await api.patch(`/portal/invoices/${id}/mark-paid`, payForm);
      toast.success("Payment recorded — receipt generated");
      setPayInv(null);
      load();
    } catch {
      toast.error("Could not record payment.");
    }
  };
  const sendToFamily = async (inv) => {
    try {
      const { data } = await api.post(`/portal/invoices/${inv.id}/send`);
      if (data.emails_sent > 0) toast.success(`Invoice emailed to family (${data.emails_sent})`);
      else toast.message("Invoice marked sent — add a client/family email to deliver it.");
      load();
    } catch {
      toast.error("Could not send the invoice.");
    }
  };
  const remove = async (id) => {
    await api.delete(`/portal/invoices/${id}`);
    toast.success("Invoice deleted");
    load();
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl text-charcoal">Invoices</h1>
          <p className="text-charcoal-soft mt-1">Itemize your care, bill your clients.</p>
        </div>
        <button onClick={openNew} disabled={loading} data-testid="add-invoice-button" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-3 text-sm font-medium hover:bg-sage-dark transition-colors disabled:opacity-60">
          <Plus className="h-4 w-4" /> New Invoice
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sage" /></div>
      ) : invoices.length === 0 ? (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow p-14 text-center">
          <FileText className="h-10 w-10 text-sage/50 mx-auto mb-4" />
          <p className="text-charcoal-soft">No invoices yet. Create your first invoice to bill a client.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-charcoal/5 soft-shadow overflow-hidden">
          <div className="divide-y divide-charcoal/5">
            {invoices.map((inv) => {
              const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.draft;
              return (
                <div key={inv.id} data-testid={`invoice-row-${inv.id}`} className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-sand-deep/30 transition-colors">
                  <button onClick={() => setViewInv(inv)} data-testid={`view-invoice-${inv.id}`} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-charcoal">{inv.invoice_number}</p>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                      {inv.receipt_number && <span className="text-xs px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{inv.receipt_number}</span>}
                    </div>
                    <p className="text-sm text-charcoal-soft">{inv.client?.name || "—"} · {inv.issue_date}</p>
                  </button>
                  <button onClick={() => setViewInv(inv)} className="font-serif text-2xl text-charcoal hover:text-sage-700 transition-colors">{currency(inv.total)}</button>
                  <div className="flex items-center gap-1">
                    <Select value={inv.status} onValueChange={(v) => changeStatus(inv, v)} disabled={inv.status === "paid"}>
                      <SelectTrigger data-testid={`status-${inv.id}`} className="h-9 w-28 bg-sand text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => setViewInv(inv)} data-testid={`eye-${inv.id}`} className="p-2 text-charcoal/40 hover:text-charcoal" title="View"><Eye className="h-4 w-4" /></button>
                    <button onClick={() => sendToFamily(inv)} data-testid={`send-${inv.id}`} className="p-2 text-charcoal/40 hover:text-sage-700" title="Email to family"><Send className="h-4 w-4" /></button>
                    <button onClick={() => printInvoice(inv)} data-testid={`print-${inv.id}`} className="p-2 text-charcoal/40 hover:text-charcoal" title="Print / PDF"><Printer className="h-4 w-4" /></button>
                    <button onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.id}`} className="p-2 text-charcoal/40 hover:text-charcoal"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(inv.id)} data-testid={`delete-invoice-${inv.id}`} className="p-2 text-charcoal/40 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <InvoiceDialog
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}
        clients={clients}
        services={services}
        editInvoice={editor.edit}
        onSaved={load}
      />

      {/* View invoice (read-only) */}
      <Dialog open={!!viewInv} onOpenChange={(o) => !o && setViewInv(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-invoice-dialog">
          {viewInv && (() => {
            const st = INVOICE_STATUS[viewInv.status] || INVOICE_STATUS.draft;
            const paid = viewInv.status === "paid";
            return (
              <div>
                <DialogHeader>
                  <DialogTitle className="font-serif text-3xl flex items-center gap-3">
                    {viewInv.invoice_number}
                    <span className={`text-xs px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="flex items-start justify-between mt-2">
                  <div>
                    <p className="text-xs text-charcoal-soft uppercase tracking-wide">Bill to</p>
                    <p className="text-lg text-charcoal">{viewInv.client?.name}</p>
                    {viewInv.client?.contact_name && <p className="text-sm text-charcoal-soft">{viewInv.client.contact_name}</p>}
                  </div>
                  <div className="text-right text-sm text-charcoal-soft">
                    <p>Issued: {viewInv.issue_date}</p>
                    {viewInv.due_date && <p>Due: {viewInv.due_date}</p>}
                  </div>
                </div>

                <table className="w-full text-sm mt-6">
                  <thead>
                    <tr className="text-charcoal-soft text-xs uppercase tracking-wide border-b border-charcoal/10">
                      <th className="text-left font-medium pb-2">Description</th>
                      <th className="text-center font-medium pb-2">Qty</th>
                      <th className="text-right font-medium pb-2">Rate</th>
                      <th className="text-right font-medium pb-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-charcoal/5">
                    {viewInv.line_items.map((li, i) => (
                      <tr key={i}>
                        <td className="py-2.5 text-charcoal">{li.description}</td>
                        <td className="py-2.5 text-center text-charcoal-soft">{li.quantity} {li.unit}</td>
                        <td className="py-2.5 text-right text-charcoal-soft">{currency(li.rate)}</td>
                        <td className="py-2.5 text-right text-charcoal">{currency(li.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-4 ml-auto w-56 space-y-1">
                  <div className="flex justify-between text-sm text-charcoal-soft"><span>Subtotal</span><span>{currency(viewInv.subtotal)}</span></div>
                  {viewInv.tax_rate ? <div className="flex justify-between text-sm text-charcoal-soft"><span>Tax ({viewInv.tax_rate}%)</span><span>{currency(viewInv.tax_amount)}</span></div> : null}
                  <div className="flex justify-between font-serif text-2xl text-charcoal border-t border-charcoal/10 pt-2"><span>Total</span><span>{currency(viewInv.total)}</span></div>
                </div>

                {viewInv.notes && <p className="text-sm text-charcoal-soft italic mt-4 border-l-2 border-sage/30 pl-3">{viewInv.notes}</p>}

                {paid && (
                  <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="receipt-panel">
                    <div className="flex items-center gap-2 text-emerald-700 font-medium mb-2"><ReceiptText className="h-4 w-4" /> Payment received · {viewInv.receipt_number}</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-charcoal">
                      <div className="flex justify-between"><span className="text-charcoal-soft">Paid on</span><span>{viewInv.paid_at || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-charcoal-soft">Method</span><span>{viewInv.payment_method || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-charcoal-soft">Paid by</span><span>{viewInv.paid_by || "—"}</span></div>
                      <div className="flex justify-between"><span className="text-charcoal-soft">Amount</span><span>{currency(viewInv.amount_paid != null ? viewInv.amount_paid : viewInv.total)}</span></div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 mt-6">
                  <button onClick={() => printInvoice(viewInv)} data-testid="view-print-invoice" className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 px-5 py-2.5 text-sm hover:bg-sand transition-colors"><Printer className="h-4 w-4" /> Print invoice</button>
                  {paid && <button onClick={() => printReceipt(viewInv)} data-testid="view-print-receipt" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 text-sm hover:bg-emerald-700 transition-colors"><ReceiptText className="h-4 w-4" /> Print receipt</button>}
                  {!paid && <button onClick={() => { setViewInv(null); changeStatus(viewInv, "paid"); }} data-testid="view-record-payment" className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-5 py-2.5 text-sm hover:bg-sage-dark transition-colors"><CreditCard className="h-4 w-4" /> Record payment</button>}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Record payment */}
      <Dialog open={!!payInv} onOpenChange={(o) => !o && setPayInv(null)}>
        <DialogContent className="bg-sand max-w-md" data-testid="record-payment-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Record Payment</DialogTitle></DialogHeader>
          <p className="text-sm text-charcoal-soft -mt-2">This marks {payInv?.invoice_number} as paid and generates a receipt.</p>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Payment method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger data-testid="pay-method" className="mt-1.5 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Cash", "Check", "Card", "Bank transfer", "Venmo / Zelle", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Paid by</Label>
              <Input data-testid="pay-by" value={payForm.paid_by} onChange={(e) => setPayForm({ ...payForm, paid_by: e.target.value })} placeholder="Name of payer" className="mt-1.5 bg-white" />
            </div>
            <div>
              <Label>Payment date</Label>
              <Input type="date" data-testid="pay-date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} className="mt-1.5 bg-white" />
            </div>
          </div>
          <button onClick={confirmPayment} data-testid="confirm-payment" className="mt-2 w-full rounded-full bg-sage text-white py-3 font-medium hover:bg-sage-dark transition-colors">Record Payment & Create Receipt</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
